import { useState, useEffect, useCallback } from 'react';
import Section from '../components/Section';
import {
  adminLogin,
  adminListPosts,
  adminCreatePost,
  adminDeletePost,
  adminSubmissions,
} from '../api';

const TOKEN_KEY = 'napho.admin.token';

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState('publish');

  useEffect(() => {
    if (!token) return;
    adminLogin(token).then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, [token]);

  if (!authed) return <Login token={token} setToken={setToken} onOk={() => setAuthed(true)} />;

  return (
    <Section title="Napho Admin" lead="Publish Market Pulse briefs and review visitor submissions.">
      <div className="admin">
        <div className="admin__tabs">
          <button className={`admin__tab ${tab === 'publish' ? 'active' : ''}`} onClick={() => setTab('publish')}>
            Market Pulse
          </button>
          <button className={`admin__tab ${tab === 'inbox' ? 'active' : ''}`} onClick={() => setTab('inbox')}>
            Submissions
          </button>
          <button
            className="admin__tab"
            style={{ marginLeft: 'auto' }}
            onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(''); setAuthed(false); }}
          >
            Sign out
          </button>
        </div>

        {tab === 'publish' ? <PublishTab token={token} /> : <InboxTab token={token} />}
      </div>
    </Section>
  );
}

// ── login ──────────────────────────────────────────────────────────────────
function Login({ token, setToken, onOk }) {
  const [value, setValue] = useState(token);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await adminLogin(value);
      sessionStorage.setItem(TOKEN_KEY, value);
      setToken(value);
      onOk();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="Admin sign-in" lead="Enter the admin token to continue.">
      <form className="form" onSubmit={submit} style={{ maxWidth: 420 }}>
        <input
          className="input"
          type="password"
          placeholder="Admin token"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
        <button className="btn btn--primary" disabled={loading}>{loading ? '…' : 'Sign in'}</button>
        {error && <p className="form__msg form__msg--err">{error}</p>}
      </form>
    </Section>
  );
}

// ── publish tab ──────────────────────────────────────────────────────────────
const EMPTY = {
  title: '', slug: '', date: '', author: 'Napho Market Desk', tags: '', summary: '', body: '',
  nifty: '', niftyChg: '', sensex: '', sensexChg: '', usdinr: '', usdinrChg: '',
};

function PublishTab({ token }) {
  const [form, setForm] = useState(EMPTY);
  const [posts, setPosts] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminListPosts(token).then((r) => setPosts(r.posts)).catch(() => {});
  }, [token]);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const dir = (chg) => (String(chg).trim().startsWith('-') ? 'down' : 'up');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const market =
      form.nifty || form.sensex || form.usdinr
        ? {
            nifty: { value: form.nifty, change: form.niftyChg, dir: dir(form.niftyChg) },
            sensex: { value: form.sensex, change: form.sensexChg, dir: dir(form.sensexChg) },
            usdinr: { value: form.usdinr, change: form.usdinrChg, dir: dir(form.usdinrChg) },
          }
        : null;
    try {
      await adminCreatePost(token, {
        title: form.title,
        slug: form.slug || undefined,
        date: form.date || undefined,
        author: form.author,
        tags: form.tags,
        summary: form.summary,
        body: form.body,
        market,
      });
      setMsg({ ok: true, text: 'Published. It is now live on Market Pulse.' });
      setForm(EMPTY);
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    try {
      await adminDeletePost(token, slug);
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    }
  };

  return (
    <div className="grid grid-2" style={{ alignItems: 'start' }}>
      <form className="form card" onSubmit={submit}>
        <h3 className="card__title">New brief</h3>
        <label className="label">Title *</label>
        <input className="input" value={form.title} onChange={set('title')} required />

        <div className="form__row">
          <div style={{ flex: 1 }}>
            <label className="label">Slug (optional)</label>
            <input className="input" value={form.slug} onChange={set('slug')} placeholder="auto from title" />
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Date</label>
            <input className="input" type="date" value={form.date} onChange={set('date')} />
          </div>
        </div>

        <div className="form__row">
          <div style={{ flex: 1 }}>
            <label className="label">Author</label>
            <input className="input" value={form.author} onChange={set('author')} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Tags (comma-separated)</label>
            <input className="input" value={form.tags} onChange={set('tags')} placeholder="Equities, RBI" />
          </div>
        </div>

        <label className="label">Summary</label>
        <input className="input" value={form.summary} onChange={set('summary')} placeholder="One-line teaser" />

        <label className="label">Body * (blank line = new paragraph)</label>
        <textarea className="textarea" value={form.body} onChange={set('body')} required style={{ minHeight: 160 }} />

        <details>
          <summary className="label" style={{ cursor: 'pointer' }}>Market snapshot (optional)</summary>
          <div className="stack mt-1">
            {[
              ['NIFTY', 'nifty', 'niftyChg'],
              ['SENSEX', 'sensex', 'sensexChg'],
              ['USD/INR', 'usdinr', 'usdinrChg'],
            ].map(([label, v, c]) => (
              <div className="form__row" key={v}>
                <input className="input" placeholder={`${label} value`} value={form[v]} onChange={set(v)} />
                <input className="input" placeholder="+0.6% / -0.1%" value={form[c]} onChange={set(c)} />
              </div>
            ))}
          </div>
        </details>

        <button className="btn btn--primary" disabled={busy}>{busy ? 'Publishing…' : 'Publish brief'}</button>
        {msg && <p className={`form__msg ${msg.ok ? 'form__msg--ok' : 'form__msg--err'}`}>{msg.text}</p>}
      </form>

      <div>
        <h3 className="card__title">Published ({posts.length})</h3>
        <div className="pulse-list">
          {posts.map((p) => (
            <div className="pulse-row" key={p.slug}>
              <div>
                <div style={{ fontWeight: 700 }}>{p.title}</div>
                <div className="muted" style={{ fontSize: '0.8rem' }}>
                  {new Date(p.published_on).toLocaleDateString()} · /{p.slug}
                </div>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={() => remove(p.slug)}>Delete</button>
            </div>
          ))}
          {posts.length === 0 && <p className="muted">No posts yet.</p>}
        </div>
      </div>
    </div>
  );
}

// ── inbox tab ──────────────────────────────────────────────────────────────
function InboxTab({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminSubmissions(token).then(setData).catch((e) => setError(e.message));
  }, [token]);

  if (error) return <p className="form__msg form__msg--err">{error}</p>;
  if (!data) return <p className="loading">Loading…</p>;

  return (
    <div className="stack">
      <div className="stat-row">
        <Stat n={data.counts.users} label="Users" />
        <Stat n={data.counts.questions} label="Questions" />
      </div>

      <Table title="Users" rows={data.users} cols={['email', 'created_at']} />
      <Table title="Questions" rows={data.questions} cols={['body', 'email', 'created_at']} />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="stat">
      <div className="stat__num">{n}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

function Table({ title, rows, cols }) {
  return (
    <div>
      <h3 className="card__title" style={{ marginTop: '1.5rem' }}>{title}</h3>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>{cols.map((c) => <th key={c}>{c.replace('_', ' ')}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c}>
                    {c === 'created_at' ? new Date(r[c]).toLocaleString() : String(r[c] ?? '-')}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length} className="muted">Nothing yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
